import React, { useState, useEffect, useMemo } from "react";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  Button,
  InputLabel,
  FormControl,
  Box,
  Paper,
  Typography,
  Stack,
  Divider,
  Chip,
  InputAdornment
} from "@mui/material";

export default function ProductForm({ producto, onAdd }) {
  const [pvp, setPvp] = useState("");
  const [ivaPorcentaje, setIvaPorcentaje] = useState(15);
  const [ivaValor, setIvaValor] = useState("0.00");
  const [descuentoPct, setDescuentoPct] = useState("0");
  const [stock, setStock] = useState(0);
  const [detalles, setDetalles] = useState("");
  const [imagen, setImagen] = useState("");
  const [brand, setBrand] = useState("");
  const [codigoPrincipal, setCodigoPrincipal] = useState("");
  const [codigoAuxiliar, setCodigoAuxiliar] = useState("");
  const [sku, setSku] = useState("");

  const normalizeDecimal = (v) => String(v || "").replace(/,/g, ".");

  const roundTo2 = (value) => {
    const num = Number(value) || 0;
    return (Math.round((num + Number.EPSILON) * 100) / 100).toFixed(2);
  };

  useEffect(() => {
    if (producto) {
      const precio = Number(producto.pvp1 || 0);
      const code = producto.code || "";
      const productSku = producto.sku || "";
      const principal = producto.codigo_principal || (code && productSku ? `${code}-${productSku}` : code);
      const porc = Number(producto.iva_porcentaje ?? 15);

      setPvp(precio.toFixed(2));
      setIvaPorcentaje(porc);
      setIvaValor(porc === 999 ? "0.00" : roundTo2((precio * porc) / 100));
      setDescuentoPct(String(Number(producto.descuentoPct || 0)));
      setStock(Number(producto.stock ?? 0));
      setDetalles("");
      setImagen(producto.imagen || "");
      setBrand(producto.brand || "");
      setCodigoPrincipal(principal);
      setCodigoAuxiliar(producto.codigo_auxiliar || "");
      setSku(productSku);
    } else {
      setPvp("");
      setIvaPorcentaje(15);
      setIvaValor("0.00");
      setDescuentoPct("0");
      setStock(0);
      setDetalles("");
      setImagen("");
      setBrand("");
      setCodigoPrincipal("");
      setCodigoAuxiliar("");
      setSku("");
    }
  }, [producto]);

  useEffect(() => {
    const precio = Number(normalizeDecimal(pvp)) || 0;
    const porc = Number(ivaPorcentaje) || 0;

    setIvaValor(
      porc === 999
        ? "0.00"
        : roundTo2((precio * porc) / 100)
    );
  }, [pvp, ivaPorcentaje]);

  const precioFinal = useMemo(() => {
    const precio = Number(normalizeDecimal(pvp)) || 0;
    const descuento = Number(normalizeDecimal(descuentoPct)) || 0;
    const precioConDescuento = Math.max(precio - (precio * descuento) / 100, 0);
    const iva = Number(normalizeDecimal(ivaValor)) || 0;
    return roundTo2(precioConDescuento + iva);
  }, [pvp, descuentoPct, ivaValor]);

  const handleAdd = () => {
    if (!producto) return;

    const precio = Number(normalizeDecimal(pvp));
    if (Number.isNaN(precio)) return;

    onAdd({
      product_id: producto.product_id ?? producto.id ?? null,
      variant_id: producto.variant_id ?? null,
      code: producto.code || "",
      sku: sku || "",
      codigo_principal: codigoPrincipal || producto.codigo_principal || producto.code || "",
      codigo_auxiliar: codigoAuxiliar || "",
      name: producto.name || "",
      variant_type: producto.variant_type || "",
      value: producto.value || "",
      pvp: precio.toFixed(2),
      iva_porcentaje: Number(ivaPorcentaje),
      iva_valor: ivaPorcentaje === 999 ? "0.00" : roundTo2((precio * Number(ivaPorcentaje || 0)) / 100),
      descuentoPct: String(Number(normalizeDecimal(descuentoPct) || 0)),
      stock: stock,
      detalles: detalles.trim(),
      is_service: producto.type === "service" || producto.code?.startsWith("S"),
      brand: brand,
      imagen: imagen,
      type: producto.type || ""
    });

    setPvp("");
    setIvaPorcentaje(15);
    setIvaValor("0.00");
    setDescuentoPct("0");
    setStock(0);
    setDetalles("");
    setImagen("");
    setBrand("");
    setCodigoPrincipal("");
    setCodigoAuxiliar("");
    setSku("");
  };

  return (
    <Grid item xs={12}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)"
        }}
      >
        <Grid container spacing={2.2} alignItems="stretch">
          <Grid item xs={12} md={8}>
            <Stack spacing={2.2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Marca"
                    value={brand}
                    disabled
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: 3,
                        backgroundColor: "#f7f9fc"
                      },
                      "& .Mui-disabled": {
                        WebkitTextFillColor: "#1f2937 !important"
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Código principal"
                    value={codigoPrincipal}
                    disabled
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: 3,
                        backgroundColor: "#f7f9fc"
                      },
                      "& .Mui-disabled": {
                        WebkitTextFillColor: "#1f2937 !important"
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Código auxiliar"
                    value={codigoAuxiliar}
                    disabled
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: 3,
                        backgroundColor: "#f7f9fc"
                      },
                      "& .Mui-disabled": {
                        WebkitTextFillColor: "#1f2937 !important"
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="SKU"
                    value={sku}
                    disabled
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: 3,
                        backgroundColor: "#f7f9fc"
                      },
                      "& .Mui-disabled": {
                        WebkitTextFillColor: "#1f2937 !important"
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Precio Unitario"
                    type="text"
                    inputProps={{ inputMode: "decimal" }}
                    value={pvp}
                    onChange={(e) => setPvp(normalizeDecimal(e.target.value))}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      sx: { borderRadius: 3 }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>IVA (%)</InputLabel>
                    <Select
                      value={ivaPorcentaje}
                      label="IVA (%)"
                      onChange={(e) => setIvaPorcentaje(Number(e.target.value))}
                      sx={{ borderRadius: 3 }}
                    >
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={5}>5%</MenuItem>
                      <MenuItem value={15}>15%</MenuItem>
                      <MenuItem value={999}>No objeto</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="IVA ($)"
                    value={ivaValor}
                    disabled
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      sx: { borderRadius: 3, backgroundColor: "#f7f9fc" }
                    }}
                    sx={{
                      "& .Mui-disabled": {
                        WebkitTextFillColor: "#1f2937 !important"
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Descuento (%)"
                    type="text"
                    inputProps={{ inputMode: "decimal" }}
                    value={descuentoPct}
                    onChange={(e) => setDescuentoPct(normalizeDecimal(e.target.value))}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      sx: { borderRadius: 3 }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Stock"
                    value={stock}
                    disabled
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: 3,
                        backgroundColor: "#f7f9fc"
                      },
                      "& .Mui-disabled": {
                        WebkitTextFillColor: "#1f2937 !important"
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Precio final"
                    value={precioFinal}
                    disabled
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      sx: { borderRadius: 3, backgroundColor: "#eef7ff" }
                    }}
                    sx={{
                      "& .Mui-disabled": {
                        WebkitTextFillColor: "#0f172a !important",
                        fontWeight: 700
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Detalles adicionales"
                    value={detalles}
                    onChange={(e) => setDetalles(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: 3
                      }
                    }}
                  />
                </Grid>
              </Grid>

              <Divider />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.2}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={producto?.type === "service" ? "Servicio" : "Producto"}
                    color={producto?.type === "service" ? "secondary" : "primary"}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  />
                  {producto?.variant_id ? (
                    <Chip
                      label={`${producto.variant_type || "Variante"}: ${producto.value || ""}`}
                      color="info"
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    />
                  ) : null}
                </Stack>

                <Button
                  variant="contained"
                  onClick={handleAdd}
                  fullWidth={false}
                  sx={{
                    minWidth: { xs: "100%", sm: 220 },
                    height: 48,
                    borderRadius: 3,
                    textTransform: "none",
                    bgcolor: "#1565C0",
                    "&:hover": { bgcolor: "#0D47A1" }
                  }}
                >
                  Añadir item
                </Button>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                height: "100%",
                minHeight: 320,
                p: 2,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "#fcfdff"
              }}
            >
              <Stack spacing={1.5} height="100%">
                <Typography variant="subtitle1" fontWeight={800}>
                  Vista previa
                </Typography>

                <Box
                  sx={{
                    flexGrow: 1,
                    minHeight: 220,
                    borderRadius: 3,
                    border: "1px dashed #cbd5e1",
                    backgroundColor: "#f8fafc",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "hidden",
                    p: 1.5
                  }}
                >
                  {imagen ? (
                    <img
                      src={imagen}
                      alt={producto?.name || "Item"}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        borderRadius: "12px"
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Selecciona un producto para ver su imagen
                    </Typography>
                  )}
                </Box>

                <Stack spacing={0.8}>
                  <Typography variant="body2" color="text.secondary">
                    {producto?.name || "Sin producto seleccionado"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Código principal:</strong> {codigoPrincipal || "—"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Código auxiliar:</strong> {codigoAuxiliar || "—"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>SKU:</strong> {sku || "—"}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
}